import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import CustomTextField from "../../../../../components/customMUIComponents/CustomTextField";
import Typography from "@mui/material/Typography";
import { Button, Chip, Grid2, MenuItem } from "@mui/material";

const SECTOR_KEY_TO_TEXT_MAP = new Map<string, string>([
    ["SIZE", "Taille"],
    ["COLOR", "Couleur"],
    ["INSCRIPTION", "Inscription"],
    ["CUSTOMIZED", "Personnalisé"],
]);

const DEFAULT_OPTIONS_MAP = new Map<string, string[]>([
    ["COLOR", ["green", "blue", "red"]],
]);

interface Variant {
    id: number;
    type: string;
    title: string;
    options: string[];
}

const ArticleVariantsDefinition = () => {
    const [variants, setVariants] = useState<Variant[]>([]);
    const [nextVariantId, setNextVariantId] = useState<number>(0);

    const handleAddVariant = useCallback(() => {
        setVariants((prevVariants) => [
            ...prevVariants,
            { id: nextVariantId, type: 'SIZE', title: SECTOR_KEY_TO_TEXT_MAP.get('SIZE') || '', options: [] },
        ]);
        setNextVariantId((prevId) => prevId + 1);
    }, [nextVariantId, setVariants]);

    const handleVariantTypeChange = useCallback(
        (id: number, newType: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) => {
                    const newTitle = newType !== 'CUSTOMIZED' ? SECTOR_KEY_TO_TEXT_MAP.get(newType) || '' : variant.title;
                    const defaultOptions = DEFAULT_OPTIONS_MAP.get(newType) || [];
                    return variant.id === id ? { ...variant, type: newType, title: newTitle, options: defaultOptions } : variant;
                })
            );
        },
        [setVariants]
    );

    const handleVariantTitleChange = useCallback(
        (id: number, newTitle: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === id ? { ...variant, title: newTitle } : variant
                )
            );
        },
        [setVariants]
    );

    const handleAddOption = useCallback(
        (id: number) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === id ? { ...variant, options: [...variant.options, 'Nouvelle option'] } : variant
                )
            );
        },
        [setVariants]
    );

    const handleDeleteOption = useCallback(
        (variantId: number, optionToDelete: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === variantId
                        ? { ...variant, options: variant.options.filter((option) => option !== optionToDelete) }
                        : variant
                )
            );
        },
        [setVariants]
    );

    const handleDeleteVariant = useCallback(
        (id: number) => {
            setVariants((prevVariants) => prevVariants.filter((variant) => variant.id !== id));
        },
        [setVariants]
    );

    return (
        <Box>
            <Typography variant={"firstVariant"} textAlign={"justify"} mb={2}>
                Les variantes d'articles peuvent être utiles si tu proposes un même article en différentes tailles,
                couleurs ou si tu proposes à l'utilisateur de le personnaliser en y gravant son prénom par exemple. Si ce
                n'est pas le cas pour cet article, tu peux ignorer cette étape.
            </Typography>

            <Typography variant={"firstVariant"} textAlign={"justify"} mb={2}>
                Nous proposons quelques options par défaut mais tu peux également créer les tiennes. Tu peux bien entendu
                ajouter plusieurs variantes. Exemple: un habit pour lequel l'utilisateur peut choisir la taille et la
                couleur.
            </Typography>

            {variants.map((variant) => (
                <Grid2 key={variant.id} container size={12} mt={3} mb={3} sx={{ border: '1px solid #ccc', padding: 2, borderRadius: 1 }}>
                    <Grid2 size={12} container spacing={2}>
                        <Grid2 size={5} container>
                            <CustomTextField
                                variant={"outlined"}
                                value={variant.type}
                                label={"Type de variante"}
                                setField={(newType) => handleVariantTypeChange(variant.id, newType)}
                                select
                                selectOptions={Array.from(SECTOR_KEY_TO_TEXT_MAP.entries()).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                                mb={2}
                                textAlign={"left"}
                                required
                            />
                        </Grid2>
                        <Grid2 size={5} container>
                            <CustomTextField
                                value={variant.title}
                                setField={(newTitle) => handleVariantTitleChange(variant.id, newTitle)}
                                label="Titre de la variante"
                                required
                                variant={"outlined"}
                                disabled={variant.type !== 'CUSTOMIZED'}
                            />
                        </Grid2>
                        <Grid2 size={2} container justifyContent="flex-end">
                            <Button onClick={() => handleDeleteVariant(variant.id)} color="error">
                                Supprimer
                            </Button>
                        </Grid2>
                    </Grid2>
                    <Grid2 size={12} container spacing={1} mt={2} alignItems="center">
                        {variant.options.map((option) => (
                            <Chip
                                key={option}
                                label={option}
                                onDelete={() => handleDeleteOption(variant.id, option)}
                            />
                        ))}
                        <Button size="small" onClick={() => handleAddOption(variant.id)} sx={{ ml: 1 }}>
                            Ajouter une option
                        </Button>
                    </Grid2>
                </Grid2>
            ))}

            <Button onClick={handleAddVariant} variant="contained" color="primary">
                Ajouter une variante
            </Button>
        </Box>
    );
};

export default ArticleVariantsDefinition;